import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { cn, dateLocale } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const PRIO = {
  high: [tfr("Élevée"), "border-rose-500/40 text-rose-600 bg-rose-500/10 dark:text-rose-300"],
  medium: [tfr("Moyenne"), "border-amber-500/40 text-amber-700 bg-amber-500/10 dark:text-amber-300"],
  low: [tfr("Basse"), "border-border text-muted-foreground bg-secondary/60"],
};
const STATUS_DOT = {
  "Repéré": "bg-slate-400", "Contacté": "bg-blue-400", "En discussion": "bg-amber-400",
  "Mandat signé": "bg-emerald-400", "Placé": "bg-teal-400",
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
};
const initials = (name) => (name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export default function StalledCasesCard({ cases, loading }) {
  const navigate = useNavigate();
  const open = (id) => navigate(`/players/${id}`);
  return (
    <section className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-4 sm:p-6" data-testid="perf-stalled-card">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-1">
        <h3 className="text-base font-semibold tracking-tight">{tfr("Dossiers en ralentissement")}</h3>
        <Link to="/pipeline" className="inline-flex items-center gap-1 text-xs font-medium text-athlynx hover:text-athlynx/80 transition-colors min-h-[32px] sm:min-h-0" data-testid="perf-stalled-all-link">
          {tfr("Voir tous les dossiers")} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{tfr("Les dossiers qui stagnent le plus dans le pipeline.")}</p>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-muted/60 animate-pulse" />)}</div>
      ) : !cases?.length ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="perf-stalled-empty">
          {tfr("Aucun dossier en ralentissement — votre pipeline avance bien.")}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <table className="w-full text-sm hidden md:table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th scope="col" className="text-left font-semibold py-2">{tfr("Athlète")}</th>
                <th scope="col" className="text-left font-semibold py-2">{tfr("Statut actuel")}</th>
                <th scope="col" className="text-right font-semibold py-2">{tfr("Temps dans l'étape")}</th>
                <th scope="col" className="text-right font-semibold py-2">{tfr("Dernière interaction")}</th>
                <th scope="col" className="text-right font-semibold py-2">{tfr("Priorité")}</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c, i) => {
                const [pl, pc] = PRIO[c.priority] || PRIO.low;
                return (
                  <tr key={c.player_id}
                    onClick={() => open(c.player_id)}
                    onKeyDown={(e) => { if (e.key === "Enter") open(c.player_id); }}
                    tabIndex={0}
                    data-testid={`perf-stalled-row-${i}`}
                    className="border-b border-border/60 cursor-pointer hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:bg-accent">
                    <td className="py-2.5 pr-2">
                      <div className="font-medium text-foreground truncate max-w-[180px]">{c.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{[c.position, c.age ? `${c.age} ${tfr("ans")}` : null].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td className="py-2.5 pr-2">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT[c.status])} aria-hidden />{tfr(c.status)}
                      </span>
                    </td>
                    <td className="py-2.5 text-right tabular-nums text-xs">
                      <span className={c.priority === "high" ? "text-rose-600 dark:text-rose-300 font-medium" : c.priority === "medium" ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground"}>
                        {c.days_in_stage} {tfr("jours")}
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-xs text-muted-foreground tabular-nums">{fmtDate(c.last_interaction_at)}</td>
                    <td className="py-2.5 text-right">
                      <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border", pc)}>{pl}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Mobile rows: athlete-first hierarchy */}
          <div className="md:hidden divide-y divide-border/70">
            {cases.map((c, i) => {
              const [pl, pc] = PRIO[c.priority] || PRIO.low;
              return (
                <button key={c.player_id} onClick={() => open(c.player_id)} data-testid={`perf-stalled-mobile-${i}`}
                  className="w-full text-left flex items-center gap-3 py-2.5 min-h-[56px] focus-visible:outline-none focus-visible:bg-accent/60 rounded-lg px-1 -mx-1">
                  <span className="h-9 w-9 rounded-full bg-secondary text-foreground/80 text-xs font-semibold flex items-center justify-center shrink-0" aria-hidden>
                    {initials(c.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground truncate">{c.name}</span>
                    <span className="block text-[11px] text-muted-foreground leading-snug">
                      {tfr(c.status)} · {c.days_in_stage} {tfr("jours dans l'étape")}
                    </span>
                    <span className="block text-[11px] text-muted-foreground/80 leading-snug">
                      {tfr("Dern. interaction :")} {fmtDate(c.last_interaction_at)}
                    </span>
                  </span>
                  <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0", pc)}>{pl}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/60 shrink-0" aria-hidden />
                </button>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
